<template>
  <div>
    <v-container class="page-title page-title--advanced">
      <v-row>
        <v-col class="page-title__title-box">
          <h4 class="page-title__title">{{ $t('heading.instance.advanced.cdn.title') }}</h4>
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row>
        <v-col cols="12" class="pb-2">
          <auto-updates-card-skeleton v-if="fetchingStatus"/>
          <v-card elevation="4" v-else>
            <v-card-text class="d-flex justify-space-between text--gray-darken2">
              <div>
                <h6>{{ $t('heading.instance.advanced.cdn.purge.title') }}</h6>
                <p class="mb-0 mt-1 pr-8">
                  {{ $t('heading.instance.advanced.cdn.purge.subtitle') }}
                </p>
                <AlertSm
                  :class="'alert-sm--primary mt-3'"
                  :text="$t('heading.instance.advanced.cdn.purge.alert')"
                />
              </div>
              <div class="d-flex align-center">
                <v-btn
                  elevation="0"
                  class="mt-0 pt-0 ms-2 font-weight-bold v-btn--default"
                  :loading="submittingPurge"
                  :disabled="!cdnEnabled"
                  @click="showPurgeModal"
                >
                  <v-icon size="16" class="font-weight-bold text--gray">$restore</v-icon>
                  <span class="text--gray font-weight-bold">{{ $t('button.purge') }}</span>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" class="pb-2">
          <auto-updates-card-skeleton v-if="fetchingStatus"/>
          <v-card elevation="4" v-else>
            <v-card-text class="d-flex justify-space-between text--gray-darken2">
              <div>
                <h6>{{ $t('heading.instance.advanced.cdn.under-attack.title') }}</h6>
                <p class="mb-6 mt-1 pr-8">
                  {{ $t('heading.instance.advanced.cdn.under-attack.subtitle') }}
                </p>
              </div>
              <div class="d-flex align-center">
                <button-switch
                  class="mt-0"
                  :loading="submittingUnderAttack"
                  :value="underAttackMode"
                  :disabled="!cdnEnabled"
                  @click.native.stop.prevent="toggleUnderAttackMode"
                />
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" class="pb-2" v-if="cdnEnabled">
          <v-card elevation="4">
            <v-card-text class="text--gray-darken2">
              <h6>{{ $t('heading.instance.advanced.cdn.features.title') }}</h6>
              <v-list dense>
                <v-list-item v-for="feature in features" :key="feature.name">
                  <v-list-item-icon class="me-3">
                    <v-icon size="18" color="success">$check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text--gray-darken2">
                      {{ feature.title || feature.name }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      :persistent="modalOptions.persistent"
      v-model="modalOptions.open"
      ref="dialog"
      transition="custom-dialog-transition"
    >
      <div
        class="card-overlay"
        v-if="!modalOptions.persistent"
        @click="modalOptions.open = !modalOptions.open"
      />
      <div class="card-overlay" v-else @click="$refs.dialog.animateClick()"/>
      <basic-modal
        style="width: 560px"
        :modalOptions="modalOptions"
        @modal-close="modalClose"
        :key="modalRender"
        ref="modal"
      />
    </v-dialog>
  </div>
</template>

<script>
import Api from "@/apis/Api";

import AutoUpdatesCardSkeleton from "../../../components/cards/AutoUpdatesCardSkeleton.vue";
import ButtonSwitch from "../../../components/buttons/ButtonSwitch.vue";
import AlertSm from "../../../components/alerts/AlertSm.vue";
import BasicModal from "../../../components/modal/BasicModal.vue";
import ActionModalMixin from "../../../mixins/ActionModalMixin";

export default {
  name: "Cdn",
  components: {
    BasicModal,
    AlertSm,
    AutoUpdatesCardSkeleton,
    ButtonSwitch,
  },
  mixins: [ActionModalMixin],
  props: {
    instance: Object,
  },
  data() {
    return {
      fetchingStatus: false,
      submittingPurge: false,
      submittingUnderAttack: false,
      underAttackMode: false,
      applicationId: this.$route.params.id,
      features: [],
    };
  },
  computed: {
    cdnEnabled() {
      return (this.instance?.plan_settings?.cdn?.provider_slug ?? "none") !== "none";
    },
  },
  mounted() {
    if (this.cdnEnabled) {
      this.fetchCdnStatus();
    }
  },
  methods: {
    fetchCdnStatus() {
      this.fetchingStatus = true;
      Api.get(`/instances/${this.applicationId}/cdn/status`)
        .then((response) => {
          this.underAttackMode = response.data.under_attack_mode;
          this.features = response.data.features ?? [];
        })
        .catch(() => {
          this.underAttackMode = false;
          this.features = [];
        })
        .finally(() => {
          this.fetchingStatus = false;
        });
    },
    showPurgeModal() {
      this.resetModal();
      this.modalOptions.icon = "$restore";
      this.modalOptions.title = this.$t('heading.instance.advanced.cdn.modal.purge.title');
      this.modalOptions.message = this.$t('heading.instance.advanced.cdn.modal.purge.info');

      this.modalOptions.buttons.unshift({
        label: this.$t('button.purge'),
        color: "primary",
        onclick: () => {
          this.purgeCache();
        },
      });
      this.modalOptions.open = true;
    },
    purgeCache() {
      this.modalOptions.persistent = true;
      this.modalOptions.submitting = true;
      this.modalOptions.submittingSuccess = "";
      this.modalOptions.submittingError = "";

      this.submittingPurge = true;

      Api.post(`/instances/${this.applicationId}/cdn/purge`)
        .then(() => {
          this.$store.dispatch("addAlert", {
            success: true,
            successMessage: this.$t("notification.instance.cdn.purge.success"),
          });
        })
        .catch((error) => {
          this.modalOptions.submittingError = Api.getErrorMessage(error);
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.modalOptions.open = false;
          this.modalOptions.persistent = false;
          this.modalOptions.submitting = false;
          this.submittingPurge = false;
        });
    },
    toggleUnderAttackMode() {
      this.submittingUnderAttack = true;
      const newValue = !this.underAttackMode;

      Api.post(`/instances/${this.applicationId}/cdn/under-attack`, {
        enabled: newValue,
      })
        .then(() => {
          this.underAttackMode = newValue;
          this.$store.dispatch("addAlert", {
            success: true,
            successMessage: newValue
              ? this.$t("notification.instance.cdn.under-attack.enable.success")
              : this.$t("notification.instance.cdn.under-attack.disable.success"),
          });
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.submittingUnderAttack = false;
        });
    },
  },
  watch: {
    cdnEnabled(newValue, oldValue) {
      if (newValue === true && oldValue === false) {
        this.fetchCdnStatus();
      }
      if (newValue === false) {
        this.underAttackMode = false;
        this.features = [];
      }
    },
  },
};
</script>